// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: the order of the search bar's channel suggestions (nested-ordering-plan.md B17), moved out of
// useMessageSearchAutocomplete so it can be tested. The current channel first, then recent visits, then the match
// rank; equal ranks follow the sidebar (tree) order instead of the name. Exclude from upstream sync.

import type {Channel} from '@app/features/channel/models/Channel';
import {matchSorter} from 'match-sorter';

export function orderChannelSuggestions(params: {
	candidates: ReadonlyArray<Channel>;
	searchTerm: string;
	displayName: (channel: Channel) => string;
	recencyRank: ReadonlyMap<string, number>;
	currentChannelId: string | undefined;
	/** channelTreeOrderIndex(candidates). */
	treeIndex: ReadonlyMap<string, number>;
}): Array<Channel> {
	const {candidates, searchTerm, displayName, recencyRank, currentChannelId, treeIndex} = params;
	const treeAt = (candidate: Channel) => treeIndex.get(candidate.id) ?? Number.MAX_SAFE_INTEGER;
	const matches = matchSorter([...candidates], searchTerm, {
		keys: [(candidate: Channel) => displayName(candidate)],
		baseSort: (a, b) => treeAt(a.item) - treeAt(b.item) || a.index - b.index,
	});
	const matchRank = new Map(matches.map((candidate, index) => [candidate.id, index]));
	const resolveRank = (candidate: Channel) => {
		if (candidate.id === currentChannelId) return -1;
		return recencyRank.get(candidate.id) ?? Number.MAX_SAFE_INTEGER;
	};
	return [...matches].sort((a, b) => {
		const rankDifference = resolveRank(a) - resolveRank(b);
		if (rankDifference !== 0) {
			return rankDifference;
		}
		return (matchRank.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (matchRank.get(b.id) ?? Number.MAX_SAFE_INTEGER);
	});
}
